import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { UploadCloud, X, Video, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const MAX_MB = 100;

const initial = {
  name: "",
  email: "",
  city: "",
  damage_type: "",
  message: "",
};

/**
 * Modal: homeowner uploads a video testimonial (mp4/mov/webm, ≤100MB).
 * On submit: video goes to object storage, JD gets an approve/decline email.
 */
export const TestimonialUploadModal = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setForm(initial);
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setDone(false);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Video must be under ${MAX_MB}MB.`);
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please pick a video first.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Name and email required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("city", form.city);
      fd.append("damage_type", form.damage_type);
      fd.append("message", form.message);
      fd.append("video", file);
      await axios.post(`${API}/testimonials`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
      });
      toast.success("Thanks! Your video is pending JD's approval.");
      setDone(true);
      setTimeout(() => {
        setOpen(false);
        reset();
      }, 3200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="testimonial-modal"
        className="max-w-xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-slate-950 border-white border-2 rounded-none shadow-[0_0_60px_rgba(125,211,252,0.3)]"
      >
        {/* Header */}
        <div
          className="relative px-6 py-5 border-b-2 border-red-600"
          style={{
            background:
              "linear-gradient(135deg, #7f1d1d 0%, #0c1a3d 55%, #1e3a8a 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <Video size={22} className="text-sky-300" />
            <div>
              <div className="font-impact uppercase text-white text-xl sm:text-2xl tracking-tight leading-none">
                Share Your Story
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-sky-300 mt-1">
                Upload a short video · Reviewed before it goes live
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            data-testid="testimonial-modal-close"
            className="absolute top-3 right-3 text-slate-300 hover:text-sky-300 transition-colors"
          >
            <X size={22} />
          </button>
          <div
            aria-hidden
            className="absolute -bottom-[2px] left-0 right-0 h-1"
            style={{
              background:
                "repeating-linear-gradient(90deg, #dc2626 0 22px, #ffffff 22px 26px, #020617 26px 50px, #1e3a8a 50px 54px)",
            }}
          />
        </div>

        {done ? (
          <div className="p-8 text-center bg-slate-950">
            <CheckCircle2 size={54} className="text-emerald-400 mx-auto mb-4" />
            <div className="font-impact uppercase text-white text-2xl tracking-tight">
              Thanks!
            </div>
            <p className="text-slate-300 mt-2">
              JD will review your video and publish it if everything looks good.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="p-6 sm:p-8 bg-slate-950 space-y-4"
            data-testid="testimonial-form"
          >
            {/* Video picker */}
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">
                Your Video (mp4 / mov / webm · under {MAX_MB}MB)
              </label>
              <div className="mt-2">
                {preview ? (
                  <div className="relative border-2 border-sky-400 bg-slate-900 p-2">
                    <video
                      src={preview}
                      controls
                      className="w-full max-h-64 bg-black"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        URL.revokeObjectURL(preview);
                        setPreview(null);
                        setFile(null);
                      }}
                      className="mt-2 text-xs uppercase tracking-wider text-red-400 hover:text-red-300 font-bold"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="testimonial-pick-video"
                    onClick={() => fileRef.current?.click()}
                    className="w-full border-2 border-dashed border-sky-400/50 hover:border-sky-400 hover:bg-sky-400/5 transition-colors p-8 flex flex-col items-center gap-3 text-slate-300 hover:text-white"
                  >
                    <UploadCloud size={40} className="text-sky-300" />
                    <div className="font-impact uppercase tracking-wider text-lg">
                      Tap to pick a video
                    </div>
                    <div className="text-xs text-slate-400">
                      Record on your phone, then pick it here
                    </div>
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  onChange={onFile}
                  className="hidden"
                  data-testid="testimonial-file-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">Full Name*</label>
                <input
                  required
                  data-testid="testimonial-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full bg-slate-900 border border-white/20 focus:border-sky-400 focus:outline-none text-white px-3 py-2.5"
                  placeholder="Jane Homeowner"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">Email*</label>
                <input
                  required
                  type="email"
                  data-testid="testimonial-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 w-full bg-slate-900 border border-white/20 focus:border-sky-400 focus:outline-none text-white px-3 py-2.5"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">City</label>
                <input
                  data-testid="testimonial-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="mt-1.5 w-full bg-slate-900 border border-white/20 focus:border-sky-400 focus:outline-none text-white px-3 py-2.5"
                  placeholder="Marietta, GA"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">Damage Type</label>
                <select
                  data-testid="testimonial-damage"
                  value={form.damage_type}
                  onChange={(e) => setForm({ ...form, damage_type: e.target.value })}
                  className="mt-1.5 w-full bg-slate-900 border border-white/20 focus:border-sky-400 focus:outline-none text-white px-3 py-2.5"
                >
                  <option value="">Select…</option>
                  <option value="wind">Wind</option>
                  <option value="hail">Hail</option>
                  <option value="tree">Tree</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-bold">Add a Note (optional)</label>
              <textarea
                data-testid="testimonial-message"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 w-full bg-slate-900 border border-white/20 focus:border-sky-400 focus:outline-none text-white px-3 py-2.5"
                placeholder="What did JD's team do for you?"
              />
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed">
              By submitting, you allow Storm Damage Pros to publish this video on the website
              once JD approves it. You can email us anytime to remove it.
            </p>

            <button
              type="submit"
              disabled={submitting || !file}
              data-testid="testimonial-submit"
              className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-impact uppercase tracking-wider text-lg py-4 border-2 border-white transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <UploadCloud size={18} /> Submit Video
                </>
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
